import type { UseProductSharePageResult } from "../hooks/useProductSharePage";
import {
  publicPortfolioFactorKey,
  type UsePublicProductStatsResult,
} from "../hooks/usePublicProductStats";
import type { FactorEntry } from "../services/productFactorsService";

/**
 * Fixture for /product-share/dev-preview (local and dev only).
 *
 * The dev backend has no product that looks like a real shared portfolio:
 * one position, a one-word description, no risk metrics, no top-portfolio
 * stats. Design work needs the page as a visitor sees it on production, so
 * this fixture carries the numbers captured from the most-visited live share
 * page (Eliant Flagship Growth Fund, captured 2026-10-05) on top of a real dev
 * product's identity. The identity (provider 3, portfolio 41) is real so the
 * performance chart and the signed-in holdings table still hit the dev API.
 */
export const PRODUCT_SHARE_DEV_PREVIEW_PRODUCT = {
  productType: "PORTFOLIO" as const,
  productId: 41,
  providerId: 3,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function daysAgo(days: number): string {
  return new Date(Date.now() - days * MS_PER_DAY).toISOString();
}

/** Deterministic daily factor series that ends at `endReturn` percent. */
function buildFactors(seed: number, endReturn: number, points = 40): FactorEntry[] {
  let x = seed * 7919 + 13;
  const random = () => {
    x = (x * 9301 + 49297) % 233280;
    return x / 233280 - 0.5;
  };
  const entries: FactorEntry[] = [];
  for (let index = 0; index < points; index += 1) {
    const progress = index / (points - 1);
    const trend = 1 + (endReturn / 100) * progress;
    const noise = index === points - 1 ? 0 : random() * 0.04;
    entries.push({
      t: new Date(Date.now() - (points - 1 - index) * 7 * MS_PER_DAY).toISOString().slice(0, 10),
      f: Math.round((trend + noise) * 10000) / 10000,
    });
  }
  return entries;
}

export const PRODUCT_SHARE_DEV_PREVIEW_SHARE: UseProductSharePageResult = {
  metadata: {
    ...PRODUCT_SHARE_DEV_PREVIEW_PRODUCT,
    providerName: "Eliant Capital",
    // The dev product's real inception, so the live chart starts where its data does.
    createdAtUtc: "2026-07-09T00:00:00.000Z",
    visibility: "PUBLIC",
  },
  aggregation: {
    title: "Eliant Flagship Growth Fund",
    description: "The Eliant Flagship Growth Fund is an actively managed thematic ETF designed to generate long-term capital appreciation by investing in companies and assets positioned to benefit from durable structural tailwinds. The fund targets asymmetric return opportunities where upside potential meaningfully outweighs downside risk, with a focus on identifying inflection points within secular growth themes. Portfolio construction is actively managed to control correlation, beta, and drawdown risk, with the objective of delivering consistent compounding across full market cycles.",
    createdAt: "2026-07-09T00:00:00.000Z",
    totalStocks: 18,
    totalLongStocks: 18,
    totalShortStocks: 0,
    totalBaskets: 5,
    totalLongBaskets: 5,
    totalShortBaskets: 0,
    totalOptions: 2,
    totalLongOptions: 2,
    totalShortOptions: 0,
    totalHoldings: 25,
    totalLongHoldings: 25,
    totalShortHoldings: 0,
  },
  // The public share endpoint discloses one stock, one option and one basket.
  stocks: [{
    conId: 4815747,
    symbol: "NVDA",
    longName: "NVIDIA Corp",
    weight: 6.5,
    secType: "STK",
    currency: "USD",
    primaryExchange: "NASDAQ",
  }],
  options: [{
    conId: 700000001,
    symbol: "AMZN",
    strikePrice: "300",
    expiryDateLocal: "20281215",
    putCall: "C",
    weight: 3.2,
  }],
  basketDetails: [{
    basketId: 9001,
    weight: 8,
    visibility: "PUBLIC",
    stocks: [],
  }],
  lastModifiedAtUtc: daysAgo(4),
  numOfChanges: [
    { span: "7d", value: 6 },
    { span: "30d", value: 31 },
    { span: "90d", value: 84 },
  ],
  riskMetrics: {
    computedAtUtc: daysAgo(1),
    params: { riskFreeRate: 0.04 },
    analyses: [{
      windowStartUtc: "2026-01-01",
      windowEndUtc: "2026-10-02",
      metrics: [
        { name: "volatility", value: 34.06 },
        { name: "maxDrawdown", value: -22.44 },
        { name: "sharpe", value: 1.92 },
        { name: "beta", value: 2.07 },
      ],
      benchmark: {
        symbol: "SPX",
        conId: 416904,
        metrics: [
          { name: "volatility", value: 12.8 },
          { name: "maxDrawdown", value: -9.1 },
          { name: "sharpe", value: 0.91 },
          { name: "beta", value: 1 },
        ],
      },
    }],
  },
  numOfLikes: 34,
  numOfComments: 2,
  providerSummary: { numOfPortfolios: 3, numOfBaskets: 5 },
  isLoading: false,
  error: null,
  errorType: null,
};

const TOP_PORTFOLIOS = [
  {
    ...PRODUCT_SHARE_DEV_PREVIEW_PRODUCT,
    title: "Eliant Flagship Growth Fund",
    providerName: "Eliant Capital",
    numOfStocks: 23,
    numOfOptions: 2,
    returnYtd: 51.98,
    seed: 5,
  },
  {
    productId: 1001,
    providerId: 10,
    title: "HFIR Energy Income Portfolio",
    providerName: "HFI Research",
    numOfStocks: 15,
    numOfOptions: 0,
    returnYtd: 38.54,
    seed: 10,
  },
  {
    productId: 1002,
    providerId: 11,
    title: "Aurelion Index",
    providerName: "Aurelion Research",
    numOfStocks: 21,
    numOfOptions: 0,
    returnYtd: 34.45,
    seed: 11,
  },
  {
    productId: 1003,
    providerId: null,
    title: "Citrindex",
    providerName: "Citrini Research",
    numOfStocks: 182,
    numOfOptions: 0,
    returnYtd: 17.35,
    seed: 9,
  },
];

export const PRODUCT_SHARE_DEV_PREVIEW_STATS: UsePublicProductStatsResult = {
  stats: {
    numOfPortfolios: 17,
    numOfBaskets: 262,
    topPortfolios: TOP_PORTFOLIOS.map((portfolio) => ({
      productId: portfolio.productId,
      providerId: portfolio.providerId,
      title: portfolio.title,
      numOfStocks: portfolio.numOfStocks,
      numOfOptions: portfolio.numOfOptions,
      performance: {
        return1D: 0.39,
        return2D: null,
        return3D: 0.39,
        return4D: null,
        return5D: null,
        return1W: 1.71,
        return2W: null,
        return1M: 0.95,
        return3M: 13.26,
        return6M: null,
        return1Y: null,
        returnYtd: portfolio.returnYtd,
        inception: portfolio.returnYtd,
        all: portfolio.returnYtd,
        computedAtUtc: daysAgo(1),
      },
    })),
  },
  providerNames: Object.fromEntries(
    TOP_PORTFOLIOS
      .filter((portfolio) => portfolio.providerId !== null)
      .map((portfolio) => [portfolio.providerId as number, portfolio.providerName])
  ),
  portfolioFactors: Object.fromEntries(
    TOP_PORTFOLIOS
      .filter((portfolio) => portfolio.providerId !== null)
      .map((portfolio) => [
        publicPortfolioFactorKey(portfolio.providerId as number, portfolio.productId),
        buildFactors(portfolio.seed, portfolio.returnYtd),
      ])
  ),
  isLoading: false,
  error: null,
};
